import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { FormControl } from "@angular/forms";
import {
  CampaignType,
  StrategyDspAudience,
  StrategyDspAudiencePreset,
  StrategyDspAudienceType,
  StrategyDspFunnel,
} from "@front/m19-api-client";
import { AccountSelectionService, AudienceId, DspAudiencesConfig, DspStrategyService } from "@front/m19-services";
import { IButtonDirective, IIconComponent, ModalService, SpinnerComponent, ToasterService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { DspAudienceListComponent } from "@m19-board/dsp/dsp-audiences-selector/dsp-audience-list/dsp-audience-list.component";
import { DspSmartAudienceSelectorComponent } from "@m19-board/dsp/dsp-smart-audience-selector/dsp-smart-audience-selector.component";
import { DspSmartAudiencesService } from "@m19-board/dsp/dsp-smart-audiences-service/dsp-smart-audiences.service";
import {
  ManualAudienceModalComponent,
  ManualAudienceModalData,
  ManualAudienceModalResult,
} from "@m19-board/dsp/manual-audience-modal/manual-audience-modal.component";
import { StrategyPageService } from "@m19-board/strategies/strategy-page/strategy-page.service";
import { concat, mergeWith, startWith, Subject } from "rxjs";
import { map, switchMap, take, tap, toArray } from "rxjs/operators";

@Component({
  selector: "app-strategy-page-dsp-audiences",
  imports: [
    IButtonDirective,
    IIconComponent,
    DspAudienceListComponent,
    TranslocoDirective,
    DspSmartAudienceSelectorComponent,
    SpinnerComponent,
  ],
  templateUrl: "./strategy-page-dsp-audiences.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyPageDspAudiencesComponent {
  private readonly strategyPageService = inject<StrategyPageService<CampaignType.DSP>>(StrategyPageService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly dspStrategiesService = inject(DspStrategyService);
  private readonly dspSmartAudiencesService = inject(DspSmartAudiencesService);
  private readonly toasterService = inject(ToasterService);
  private readonly translationService = inject(TranslocoService);
  private readonly modalService = inject(ModalService);

  protected readonly StrategyDspAudienceType = StrategyDspAudienceType;

  strategy = this.strategyPageService.strategy;
  strategy$ = toObservable(this.strategy);

  readonly isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$, {
    initialValue: false,
  });

  readonly allowPreset = computed(() => this.strategy()?.dspFunnel !== StrategyDspFunnel.AWARENESS);

  readonly isUpdatingAudiences = signal(false);

  readonly audiencePresetControl = new FormControl<StrategyDspAudiencePreset>(StrategyDspAudiencePreset.MANUAL, {
    nonNullable: true,
  });

  readonly updatedAudienceWhitelistSubject = new Subject<StrategyDspAudience[]>();
  readonly updatedAudienceBlacklistSubject = new Subject<StrategyDspAudience[]>();
  readonly strategyAudienceWhitelist$ = this.strategy$.pipe(
    map((strategy) => strategy?.dspAudiences ?? []),
    mergeWith(this.updatedAudienceWhitelistSubject),
    map((strategyAudiences) => {
      return strategyAudiences.reduce((acc: StrategyDspAudience[][], audience) => {
        if (audience.type !== StrategyDspAudienceType.WHITELIST) {
          return acc;
        }
        if (!acc[audience.group]) {
          acc[audience.group] = [];
        }
        acc[audience.group].push(audience);
        return acc;
      }, []);
    }),
    startWith([]),
  );

  readonly strategyAudienceBlacklist$ = this.strategy$.pipe(
    map((strategy) => strategy?.dspAudiences ?? []),
    mergeWith(this.updatedAudienceBlacklistSubject),
    map((strategyAudiences) => {
      return [strategyAudiences.filter((audience) => audience.type === StrategyDspAudienceType.BLACKLIST)];
    }),
    startWith([]),
  );

  readonly whitelistedAudiences = toSignal(this.strategyAudienceWhitelist$, { requireSync: true });
  readonly blacklistedAudiences = toSignal(this.strategyAudienceBlacklist$, { requireSync: true });
  readonly existingAudienceConfig = computed<DspAudiencesConfig | undefined>(() => ({
    whitelistAudiences: this.whitelistedAudiences(),
    blacklistAudiences: this.blacklistedAudiences(),
  }));

  constructor() {
    // TODO(joseph): replace by signals forms once available
    effect(() => {
      if (!this.allowPreset()) {
        this.audiencePresetControl.setValue(StrategyDspAudiencePreset.MANUAL);
      } else if (this.strategy()?.audiencePreset) {
        this.audiencePresetControl.setValue(this.strategy()!.audiencePreset!);
      }
    });
  }

  openManualAudienceCreationModal() {
    if (this.isReadOnly()) return;
    if (this.strategy()?.audiencePreset === StrategyDspAudiencePreset.MANUAL) {
      this.audiencePresetControl.setValue(StrategyDspAudiencePreset.MANUAL);
      return;
    }

    this.modalService
      .openModal<ManualAudienceModalData, ManualAudienceModalResult>(ManualAudienceModalComponent, {
        modalTitle: this.translationService.translate("dsp-audiences-selector.create_audiences"),
        data: {
          mode: "creation",
        },
      })
      .afterClosed()
      .pipe(
        tap((_) => this.isUpdatingAudiences.set(true)),
        switchMap(({ whitelistChanges, blacklistChanges }) =>
          this.updateAudiencesFromPreset(
            StrategyDspAudiencePreset.MANUAL,
            whitelistChanges.toAdd,
            blacklistChanges.toAdd,
          ),
        ),
      )
      .subscribe({
        next: () => {
          this.toasterService.success(this.translationService.translate("dsp-strategy-page.update_audiences_success"));
          this.isUpdatingAudiences.set(false);
        },
        error: () => {
          this.toasterService.error(this.translationService.translate("dsp-strategy-page.update_audiences_error"));
          this.isUpdatingAudiences.set(false);
        },
      });
  }

  openManualAudienceEditModal() {
    if (this.isReadOnly()) return;
    this.modalService
      .openModal<ManualAudienceModalData, ManualAudienceModalResult>(ManualAudienceModalComponent, {
        modalTitle: this.translationService.translate("dsp-audiences-selector.manage_audiences"),
        data: {
          mode: "edit",
          isAudienceRequired: this.strategy()?.dspFunnel !== StrategyDspFunnel.AWARENESS,
          whitelistAudiences: this.whitelistedAudiences(),
          blacklistAudiences: this.blacklistedAudiences(),
        },
      })
      .afterClosed()
      .pipe(
        tap((_) => this.isUpdatingAudiences.set(true)),
        switchMap(({ whitelistChanges, blacklistChanges }) =>
          concat(
            this.dspStrategiesService
              .updateStrategyDspAudiences(
                this.strategy()!.strategyId!,
                whitelistChanges.toAdd,
                whitelistChanges.toRemove,
                StrategyDspAudienceType.WHITELIST,
              )
              .pipe(take(1)),
            this.dspStrategiesService
              .updateStrategyDspAudiences(
                this.strategy()!.strategyId!,
                blacklistChanges.toAdd,
                blacklistChanges.toRemove,
                StrategyDspAudienceType.BLACKLIST,
              )
              .pipe(take(1)),
          ).pipe(toArray()),
        ),
      )
      .subscribe({
        next: ([whitelistAudiences, blacklistAudiences]) => {
          this.updatedAudienceWhitelistSubject.next(whitelistAudiences);
          this.updatedAudienceBlacklistSubject.next(blacklistAudiences);
          this.toasterService.success(this.translationService.translate("dsp-strategy-page.update_audiences_success"));
          this.isUpdatingAudiences.set(false);
        },
        error: () => {
          this.toasterService.error(this.translationService.translate("dsp-strategy-page.update_audiences_error"));
          this.isUpdatingAudiences.set(false);
        },
      });
  }

  saveSmartAudienceSelection() {
    if (this.isReadOnly()) return;
    this.isUpdatingAudiences.set(true);
    this.dspSmartAudiencesService
      .createAudiencesFromSmartAudiencePreset(
        this.strategy()!.name!,
        this.strategy()!.asins.map((a) => a.asin),
        this.audiencePresetControl.value,
      )
      .pipe(
        switchMap((audiences) => {
          return this.updateAudiencesFromPreset(
            this.audiencePresetControl.value,
            audiences.whitelistAudiences,
            audiences.blacklistAudiences,
          );
        }),
      )
      .subscribe({
        next: () => {
          this.toasterService.success(this.translationService.translate("dsp-strategy-page.update_audiences_success"));
          this.isUpdatingAudiences.set(false);
        },
        error: () => {
          this.toasterService.error(this.translationService.translate("dsp-strategy-page.update_audiences_error"));
          this.isUpdatingAudiences.set(false);
        },
      });
  }

  private updateAudiencesFromPreset(
    preset: StrategyDspAudiencePreset,
    whitelistAudiences: AudienceId[][],
    blacklistAudiences: AudienceId[][],
  ) {
    return this.dspStrategiesService
      .replaceStrategyDspAudiences(this.strategy()!, whitelistAudiences, blacklistAudiences)
      .pipe(
        tap(() => this.isUpdatingAudiences.set(true)),
        switchMap(() =>
          this.dspStrategiesService.updateDspStrategyAudiencePreset(this.strategy()!.strategyId!, preset),
        ),
      );
  }

  cancelChanges() {
    this.audiencePresetControl.setValue(this.strategy()?.audiencePreset ?? StrategyDspAudiencePreset.MANUAL);
  }
}
